"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const store = require("@tanstack/store");
const asyncRetryer = require("./async-retryer.cjs");
const utils = require("./utils.cjs");
const eventClient = require("./event-client.cjs");
function getDefaultAsyncDebouncerState() {
  return {
    canLeadingExecute: true,
    errorCount: 0,
    isExecuting: false,
    isPending: false,
    lastArgs: void 0,
    lastResult: void 0,
    maybeExecuteCount: 0,
    settleCount: 0,
    status: "idle",
    successCount: 0
  };
}
function asyncDebouncerOptions(options) {
  return options;
}
const defaultOptions = {
  asyncRetryerOptions: {
    maxAttempts: 1
  },
  enabled: true,
  leading: false,
  trailing: true,
  wait: 0
};
class AsyncDebouncer {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this.store = new store.Store(getDefaultAsyncDebouncerState());
    this.asyncRetryers = /* @__PURE__ */ new Map();
    this.#timeoutId = null;
    this.#resolvePreviousPromise = null;
    this._emit = () => eventClient.emitChange("AsyncDebouncer", this);
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
      if (!this.#getEnabled()) {
        this.cancel();
      }
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const { isPending, isExecuting, settleCount } = combinedState;
        return {
          ...combinedState,
          status: !this.#getEnabled() ? "disabled" : isPending ? "pending" : isExecuting ? "executing" : settleCount > 0 ? "settled" : "idle"
        };
      });
      eventClient.emitChange("AsyncDebouncer", this);
    };
    this.#getEnabled = () => {
      return !!utils.parseFunctionOrValue(this.options.enabled, this);
    };
    this.#getWait = () => {
      return utils.parseFunctionOrValue(this.options.wait, this);
    };
    this.maybeExecute = async (...args) => {
      if (!this.#getEnabled()) return void 0;
      this.#cancelPendingExecution();
      this.#setState({
        lastArgs: args,
        maybeExecuteCount: this.store.state.maybeExecuteCount + 1
      });
      if (this.options.leading && this.store.state.canLeadingExecute) {
        this.#setState({ canLeadingExecute: false });
        await this.#execute(...args);
        return this.store.state.lastResult;
      }
      if (this.options.trailing && this.#getEnabled()) {
        this.#setState({ isPending: true });
      }
      return new Promise((resolve, reject) => {
        this.#resolvePreviousPromise = resolve;
        this.#timeoutId = setTimeout(async () => {
          if (this.options.trailing && this.store.state.lastArgs) {
            try {
              await this.#execute(...this.store.state.lastArgs);
            } catch (error) {
              reject(error);
            }
          }
          this.#setState({ canLeadingExecute: true });
          this.#resolvePreviousPromise = null;
          resolve(this.store.state.lastResult);
        }, this.#getWait());
      });
    };
    this.#execute = async (...args) => {
      if (!this.#getEnabled()) return void 0;
      const currentMaybeExecuteCount = this.store.state.maybeExecuteCount + 1;
      try {
        this.#setState({ isExecuting: true });
        const currentAsyncRetryer = new asyncRetryer.AsyncRetryer(this.fn, {
          ...this.options.asyncRetryerOptions,
          key: `${this.key}-retryer-${currentMaybeExecuteCount}`
        });
        this.asyncRetryers.set(currentMaybeExecuteCount, currentAsyncRetryer);
        const result = await currentAsyncRetryer.execute(...args);
        this.#setState({
          lastResult: result,
          successCount: this.store.state.successCount + 1
        });
        this.options.onSuccess?.(result, args, this);
      } catch (error) {
        this.#setState({
          errorCount: this.store.state.errorCount + 1
        });
        this.options.onError?.(error, args, this);
        if (this.options.throwOnError) {
          throw error;
        }
      } finally {
        this.asyncRetryers.delete(currentMaybeExecuteCount);
        this.#setState({
          isExecuting: false,
          isPending: false,
          lastArgs: void 0,
          settleCount: this.store.state.settleCount + 1
        });
        this.options.onSettled?.(args, this);
      }
      return this.store.state.lastResult;
    };
    this.flush = async () => {
      if (this.store.state.isPending && this.store.state.lastArgs) {
        const { lastArgs } = this.store.state;
        this.#cancelPendingExecution();
        return await this.#execute(...lastArgs);
      }
      return void 0;
    };
    this.#resolvePreviousPromiseInternal = () => {
      if (this.#resolvePreviousPromise) {
        this.#resolvePreviousPromise(this.store.state.lastResult);
        this.#resolvePreviousPromise = null;
      }
    };
    this.#clearTimeout = () => {
      if (this.#timeoutId) {
        clearTimeout(this.#timeoutId);
        this.#timeoutId = null;
      }
    };
    this.#cancelPendingExecution = () => {
      this.#clearTimeout();
      this.#resolvePreviousPromiseInternal();
      this.#setState({
        isPending: false,
        lastArgs: void 0
      });
    };
    this.abort = () => {
      this.asyncRetryers.forEach((retryer) => retryer.abort());
      this.asyncRetryers.clear();
      this.#setState({
        isExecuting: false
      });
    };
    this.cancel = () => {
      this.#cancelPendingExecution();
      this.#setState({ canLeadingExecute: true });
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncDebouncerState());
      this.asyncRetryers.forEach((retryer) => retryer.reset());
    };
    this.key = utils.createKey(initialOptions.key);
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? !initialOptions.onError
    };
    this.#setState(this.options.initialState ?? {});
    eventClient.pacerEventClient.on("d-AsyncDebouncer", (event) => {
      if (event.payload.key !== this.key) return;
      this.#setState(event.payload.store.state);
      this.setOptions(event.payload.options);
    });
  }
  #timeoutId;
  #resolvePreviousPromise;
  #setState;
  #getEnabled;
  #getWait;
  #execute;
  #resolvePreviousPromiseInternal;
  #clearTimeout;
  #cancelPendingExecution;
  /**
   * Returns the AbortSignal for a specific execution.
   * If no maybeExecuteCount is provided, returns the signal for the most recent execution.
   * Returns null if no execution is found or not currently executing.
   *
   * @param maybeExecuteCount - Optional specific execution to get signal for
   * @example
   * ```typescript
   * const debouncer = new AsyncDebouncer(
   *   async (searchTerm: string) => {
   *     const signal = debouncer.getAbortSignal()
   *     if (signal) {
   *       const response = await fetch(`/api/search?q=${searchTerm}`, { signal })
   *       return response.json()
   *     }
   *   },
   *   { wait: 300 }
   * )
   * ```
   */
  getAbortSignal(maybeExecuteCount) {
    const count = maybeExecuteCount ?? this.store.state.maybeExecuteCount;
    const retryer = this.asyncRetryers.get(count);
    return retryer?.getAbortSignal() ?? null;
  }
}
function asyncDebounce(fn, initialOptions) {
  const asyncDebouncer = new AsyncDebouncer(fn, initialOptions);
  return asyncDebouncer.maybeExecute;
}
exports.AsyncDebouncer = AsyncDebouncer;
exports.asyncDebounce = asyncDebounce;
exports.asyncDebouncerOptions = asyncDebouncerOptions;
//# sourceMappingURL=async-debouncer.cjs.map
